// Badge display helpers (Tasks 13.3, 13.4, 13.6).
//
// Maps BadgeId values to their human-readable names and computes next-badge
// progress for the profile (Req 30.8). Threshold data is sourced from the
// shared domain `BADGE_THRESHOLDS` so the client never duplicates milestone
// values.

import { BADGE_THRESHOLDS, type BadgeId } from "@sharebite/domain";

export const BADGE_NAMES: Record<BadgeId, string> = {
  FirstDonation: "First Donation",
  CommunityContributor: "Community Contributor",
  FoodHero: "Food Hero",
  HungerFighter: "Hunger Fighter",
  ShareBiteChampion: "ShareBite Champion",
  FirstCollection: "First Collection",
  CommunityHelper: "Community Helper",
  HungerReliefPartner: "Hunger Relief Partner",
  ImpactMaker: "Impact Maker",
};

export function badgeName(id: BadgeId): string {
  return BADGE_NAMES[id] ?? id;
}

export interface NextBadgeProgress {
  currentMeals: number;
  threshold: number;
  badgeName: string;
}

/**
 * Compute the lowest unearned meals-based badge for a Restaurant or NGO
 * (Req 30.8). Returns null when every meals-based badge for the role is earned.
 */
export function nextMealsBadge(
  role: "Restaurant" | "NGO",
  totalMeals: number,
): NextBadgeProgress | null {
  const candidates = BADGE_THRESHOLDS.filter(
    (t) => t.metric === "meals" && t.roles.includes(role),
  ).sort((a, b) => a.threshold - b.threshold);

  for (const t of candidates) {
    if (totalMeals < t.threshold) {
      return {
        currentMeals: totalMeals,
        threshold: t.threshold,
        badgeName: badgeName(t.badgeId),
      };
    }
  }
  return null;
}

/** Format the next-badge progress line, e.g. "Meals Donated: 245 / 500, Next Badge: Hunger Fighter". */
export function formatNextBadge(
  role: "Restaurant" | "NGO",
  progress: NextBadgeProgress,
): string {
  const label = role === "Restaurant" ? "Meals Donated" : "Meals Received";
  return `${label}: ${progress.currentMeals} / ${progress.threshold}, Next Badge: ${progress.badgeName}`;
}
